import dayjs, { type Dayjs } from 'dayjs'

import { detect } from './detect.ts'
import type { Timeframe } from './timeframe.ts'
//
// 2021-01のような文字列を解析して、startAt, endAtを返す関数
// 例えば、2021-01に対しては、startAt: 2021-01-01, endAt: 2021-01-31の値を返す（実際の値はdayjsのインスタンス）
// 2021-1Q, 2021-01-01Wのような四半期、週のような特殊な日付け意識に対応しているのが特徴
//
export function parse(
  dateString: string,
  option?: { accountClosingMonth: number; timeframe?: Timeframe },
): { startAt: Dayjs; endAt: Dayjs } | undefined {
  const detected = detect(dateString)
  if (detected === undefined) {
    return
  }

  switch (detected.timeframe) {
    case 'day': {
      return {
        startAt: detected.date,
        endAt: detected.date.endOf('day'),
      }
    }
    case 'week': {
      // dayjsは日曜始まりのカレンダーなので+1で月曜始まりにする
      const startAt = detected.date.startOf('week').add(1, 'day')
      return {
        startAt,
        endAt: startAt.add(6, 'day').endOf('day'),
      }
    }
    case 'month': {
      return {
        startAt: detected.date,
        endAt: detected.date.endOf('month'),
      }
    }
    case 'fiscal_half': {
      // eslint-disable-next-line prefer-named-capture-group
      const result = /(\d{4})-(上半期|下半期)/v.exec(dateString)
      const year = result?.[1]
      const half = result?.[2]
      if (year === undefined || half === undefined) {
        return
      }
      const halfStartAt = dayjs(`${year}-01-01`).add((option?.accountClosingMonth ?? 0) % 12, 'month')
      const monthOffset = half === '上半期' ? 0 : 6
      const startAt = halfStartAt.add(monthOffset, 'month')
      return {
        startAt,
        endAt: startAt.add(5, 'month').endOf('month'),
      }
    }
    case 'fiscal_quarter': {
      // eslint-disable-next-line prefer-named-capture-group
      const result = /(\d{4})-([1-4])Q/v.exec(dateString)
      const year = result?.[1]
      const quarter = result?.[2]
      if (year === undefined || quarter === undefined) {
        return
      }
      // 決算月が3月のとき、3ヶ月加えて4月始まりが正しい
      // yearが2022、決算月が12月のとき、2022-1Qは2022年1月。
      const quarterStartAt = dayjs(`${year}-01-01`).add((option?.accountClosingMonth ?? 0) % 12, 'month')
      const startAt = quarterStartAt.add((Number.parseInt(quarter, 10) - 1) * 3, 'month')
      return {
        startAt,
        endAt: startAt.add(2, 'month').endOf('month'),
      }
    }
    case 'year': {
      if (option?.timeframe === 'fiscal_year') {
        const startAt = detected.date.add(option.accountClosingMonth % 12, 'month')
        return {
          startAt,
          endAt: startAt.add(11, 'month').endOf('month'),
        }
      }
      const startAt = detected.date
      return {
        startAt,
        endAt: startAt.endOf('year'),
      }
    }
    // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
    default: {
      // eslint-disable-next-line sonarjs/no-dead-store,sonarjs/no-unused-vars
      const x: never = detected
    }
  }
}
